/**
 * 素材图：未单独上传时，从第一色主图居中裁 1:1（≥800）并转 Temu URL。
 * 对齐平台 materialImgUrl（errorCode 2000017）。
 */

import { temuPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { prepareTemuMaterialImage } from '@/temu-platform/utils/temuProductImage';
import { toCanonicalUploadsPath } from '@/temu-platform/modules/products/components/publishImageUploadThumbSrc';
import { isSignedViewPath } from '@/utils/imageDisplayAccess';
import { resolveImageUrlToBlob } from '@/utils/imageUrlUtils';
import { isLikelyBrowserReachableThumb, pickBrowserPreviewImageUrl } from '@/temu-platform/modules/products/components/TemuProductThumb';
import { isStrictTemuImageCdnUrl } from '@/pages/ipe/workbench/utils/convertListingImageToTemu';

function imageUrlOf(image) {
  if (!image) return '';
  return String(
    image.temuUrl
    || image.publicUrl
    || image.ipeUrl
    || image.previewUrl
    || image.url
    || '',
  ).trim();
}

function temuUrlOf(image) {
  if (!image) return '';
  return String(image.temuUrl || '').trim();
}

function looksLikeTemuHostedUrl(url) {
  return isStrictTemuImageCdnUrl(url);
}

function buildUploadedSlot(file, res, { requireTemuUrl = true } = {}) {
  const temuUrl = String(res?.temuUrl || res?.url || '').trim();
  if (requireTemuUrl && !temuUrl) {
    throw new Error(res?.message || res?.msg || '素材图上传未返回 TEMU 地址，请重试');
  }
  const rawIpe = String(res?.ipeUrl || res?.originalUrl || res?.storedUrl || '').trim();
  const rawPublic = String(
    res?.publicUrl
    || res?.previewUrlDisplay
    || res?.originalUrlDisplay
    || res?.viewUrl
    || res?.displayUrl
    || '',
  ).trim();
  const durable = toCanonicalUploadsPath(rawIpe)
    || toCanonicalUploadsPath(rawPublic)
    || rawIpe
    || rawPublic
    || '';
  let previewUrl = '';
  try {
    if (typeof URL !== 'undefined' && file) {
      previewUrl = URL.createObjectURL(file);
    }
  } catch {
    /* ignore */
  }
  if (!previewUrl) {
    const fallback = pickBrowserPreviewImageUrl(rawPublic, durable, rawIpe, temuUrl);
    previewUrl = (fallback && isLikelyBrowserReachableThumb(fallback)) ? fallback : durable;
  }
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: file?.name || 'material.jpg',
    previewUrl,
    publicUrl: isSignedViewPath(rawPublic) ? rawPublic : durable,
    ipeUrl: durable,
    temuUrl,
    _fromMainCrop: true,
  };
}

/**
 * 从槽位读成 File。优先站内/签名地址，避开 Temu CDN（浏览器 CORS 常失败）。
 */
export async function fileFromTemuImageSlot(slot) {
  if (!slot || typeof slot !== 'object') return null;
  const candidates = [
    slot.previewUrl,
    slot.ipeUrl,
    slot.publicUrl,
    slot.url,
    slot.temuUrl,
  ]
    .map((u) => String(u || '').trim())
    .filter(Boolean);
  if (!candidates.length) return null;

  const ordered = [
    ...candidates.filter((u) => !looksLikeTemuHostedUrl(u)),
    ...candidates.filter((u) => looksLikeTemuHostedUrl(u)),
  ];
  const seen = new Set();
  for (const url of ordered) {
    if (seen.has(url)) continue;
    seen.add(url);
    try {
      const blob = await resolveImageUrlToBlob(url, slot);
      if (!blob) continue;
      return new File([blob], slot.name || 'main.jpg', {
        type: blob.type || 'image/jpeg',
        lastModified: Date.now(),
      });
    } catch {
      /* try next candidate */
    }
  }
  return null;
}

function pickFirstColorMainSlot(form) {
  const skcList = Array.isArray(form?.skcList) ? form.skcList : [];
  // 仅第一个颜色的主图用于素材图自动裁切
  const first = skcList[0];
  return imageUrlOf(first?.mainImage) ? first.mainImage : null;
}

/**
 * 产品模板是否已有可用素材图（站内或平台地址均可）。
 */
export function formHasTemuMaterialImage(form) {
  const material = form?.materialImage;
  if (!material) return false;
  const materialUrl = imageUrlOf(material);
  if (!materialUrl) return false;
  const mainUrl = imageUrlOf(pickFirstColorMainSlot(form));
  // 旧逻辑把主图链直接塞进素材图：不算独立素材图
  if (mainUrl && materialUrl === mainUrl) return false;
  const materialTemu = temuUrlOf(material);
  const mainTemu = temuUrlOf(pickFirstColorMainSlot(form));
  if (materialTemu && mainTemu && materialTemu === mainTemu && !material._fromMainCrop) {
    return false;
  }
  return true;
}

/**
 * 从主图槽位派生 1:1 素材图并上传。
 * @returns {Promise<object|null>} materialImage slot
 */
export async function deriveTemuMaterialFromMainSlot({
  shopId,
  mainSlot,
  skipTemuConvert = false,
} = {}) {
  if (!mainSlot) return null;
  const sourceFile = await fileFromTemuImageSlot(mainSlot);
  if (!sourceFile) {
    throw new Error('无法读取主图以生成素材图，请重新上传主图或手动上传素材图');
  }
  // requireAspect=false：任意比例主图居中裁成 1:1≥800
  const cropped = await prepareTemuMaterialImage(sourceFile, { requireAspect: false });
  if (skipTemuConvert) {
    const res = await adminAPI.storeOfficialTemuProductTemplateImage(cropped);
    return buildUploadedSlot(cropped, res, { requireTemuUrl: false });
  }
  if (!shopId) {
    throw new Error('请先选择店铺后再生成素材图');
  }
  // 素材裁剪图仅上 Temu，不写入 OSS/图片空间
  const res = await temuPlatformAPI.uploadProductPic(shopId, cropped, {
    imageBizType: 0,
    fromImageSpace: true,
  });
  return buildUploadedSlot(cropped, res, { requireTemuUrl: true });
}

/**
 * 发布/存草稿前：优先保留模板/手传素材图；否则用第一个颜色的主图自动裁成 1:1。
 */
export async function ensureTemuMaterialFromMainOnForm(form, {
  shopId,
  skipTemuConvert = false,
} = {}) {
  const mainSlot = pickFirstColorMainSlot(form);
  const existing = form?.materialImage || null;
  const materialTemu = temuUrlOf(existing);
  const mainTemu = temuUrlOf(mainSlot);

  // 已有独立素材图平台地址（必须是图片 CDN，卖家页/站内地址要重新转）
  if (materialTemu && isStrictTemuImageCdnUrl(materialTemu) && materialTemu !== mainTemu) {
    return form;
  }

  // 模板/手传素材图仅有站内地址：直接转传该图（勿再从主图裁）
  const existingUrl = imageUrlOf(existing);
  const mainUrl = imageUrlOf(mainSlot);
  if (existing && existingUrl && existingUrl !== mainUrl) {
    const materialImage = await deriveTemuMaterialFromMainSlot({
      shopId,
      mainSlot: existing,
      skipTemuConvert,
    });
    if (!materialImage) return form;
    return {
      ...form,
      materialImage,
      materialImgUrl: String(materialImage.temuUrl || '').trim(),
    };
  }

  if (!mainSlot) return form;
  const materialImage = await deriveTemuMaterialFromMainSlot({
    shopId,
    mainSlot,
    skipTemuConvert,
  });
  if (!materialImage) return form;
  return {
    ...form,
    materialImage,
    materialImgUrl: String(materialImage.temuUrl || '').trim(),
  };
}
